import { Dropdown } from "antd";
import moment from "moment-timezone";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useSWRConfig } from "swr";
import UserAlertListDropdown from "rivals/components/app/Layout/Header/AccountLinks/UserAlerts/UserAlertListDropdown";
import UserAlertListItem from "rivals/components/app/Layout/Header/AccountLinks/UserAlerts/UserAlertListItem";
import BellIcon from "rivals/components/shared/Icons/BellIcon";
import useGetHasUnviewedUserAlerts from "rivals/services/getHasUnviewedUserAlerts/useGetHasUnviewedUserAlerts";
import useGetUserAlerts, {
  BASE_URL_USERS_ALERTS
} from "rivals/services/getUserAlerts/useGetUserAlerts";
import useMarkAllUserAlertsRead from "rivals/services/markAllUserAlertsRead/markAllUserAlertsRead";
import useMarkAllUserAlertsViewed from "rivals/services/markAllUserAlertsViewed/markAllUserAlertsViewed";
import useMarkUserAlertRead from "rivals/services/markUserAlertRead/markUserAlertRead";
import type { UserAlert } from "rivals/shared/interfaces/UserAlert";
import { instantiateScrollableObserver } from "rivals/shared/utils/instantiateScrollableObserver";
import dropdownStyles from "./UserAlertListDropdown/UserAlertListDropdown.module.scss";
import styles from "./UserAlerts.module.scss";

const INITIAL_PAGE_COUNT = 0;

export default function UserAlerts(): ReactNode | null {
  const { trigger: markUserAlertsViewed } = useMarkAllUserAlertsViewed();
  const { trigger: markUserAlertsRead } = useMarkAllUserAlertsRead();
  const { trigger: markUserAlertRead } = useMarkUserAlertRead();
  const [hasOpenedAlertsOnce, setHasOpenedAlertsOnce] = useState(false);
  const [startDateSeconds, setStartDateSeconds] = useState<number | null>(null);
  const [hasReadAllLocally, setHasReadAllLocally] = useState(false);
  const [locallyReadAlertsByIndex, setLocallyReadAlertsByIndex] = useState<
    number[]
  >([]);
  const scrollableObserverRef = useRef<IntersectionObserver | null>(null);
  const { mutate } = useSWRConfig();
  // The closure of the intersection observer callback becomes stale, so store the page count in a ref
  const pageCountRef = useRef(INITIAL_PAGE_COUNT);

  const {
    data: userAlertPages,
    setSize: setPageCount,
    isValidating: isFetchingAlerts
  } = useGetUserAlerts(
    { startDateSeconds },
    {
      initialSize: INITIAL_PAGE_COUNT,
      revalidateAll: false,
      revalidateFirstPage: false,
      revalidateIfStale: true,
      revalidateOnFocus: false
    }
  );

  const { data: hasUnviewedUserAlerts } = useGetHasUnviewedUserAlerts({
    revalidateOnFocus: false
  });
  const hasUnviewed = hasUnviewedUserAlerts?.hasUnviewed;

  // Once alert dropdown populates the DOM, instantiate the page end scrollable observer
  useEffect(() => {
    if (hasOpenedAlertsOnce) {
      scrollableObserverRef.current = instantiateScrollableObserver(
        dropdownStyles.infiniteScrollContainer,
        dropdownStyles.infiniteScrollLoadingGraphic,
        (entries: IntersectionObserverEntry[]): void => {
          if (entries[0].isIntersecting) {
            // Increment page size when page end is reached
            pageCountRef.current++;
            setPageCount(pageCountRef.current);
          }
        }
      );
    }
  }, [hasOpenedAlertsOnce, setPageCount]);

  const onToggleDropdown = (open: boolean): void => {
    if (!hasOpenedAlertsOnce) {
      setHasOpenedAlertsOnce(true);
    }

    if (open) {
      setStartDateSeconds(moment().unix());
      markUserAlertsViewed();
    }

    if (!open) {
      pageCountRef.current = INITIAL_PAGE_COUNT;
      setPageCount(INITIAL_PAGE_COUNT);
      clearUserAlertPagesCache();
      setStartDateSeconds(null);
      setHasReadAllLocally(false);
      setLocallyReadAlertsByIndex([]);
    }
  };

  const onClickMarkUserAlertsRead = (): void => {
    markUserAlertsRead();
    setHasReadAllLocally(true);
  };

  const onClickAlert = (clickedUserAlert: UserAlert, index: number): void => {
    // Update read status of content alerts only, since forums alerts are marked read when visiting the forums page
    if (
      clickedUserAlert.alertSource == "content" &&
      !isAlertRead(clickedUserAlert, index) &&
      clickedUserAlert.contentAlertId
    ) {
      markUserAlertRead(clickedUserAlert.contentAlertId);
    }

    setLocallyReadAlertsByIndex([...locallyReadAlertsByIndex, index]);

    if (clickedUserAlert.alertUrl) {
      window.open(clickedUserAlert.alertUrl, "_self");
    }
  };

  const clearUserAlertPagesCache = (): void => {
    mutate(
      key =>
        typeof key === "string" &&
        key.includes(`${BASE_URL_USERS_ALERTS}?page=`),
      undefined,
      { revalidate: false }
    );
  };

  const isAlertRead = (alert: UserAlert, index: number): boolean => {
    return (
      !!alert.readDate ||
      hasReadAllLocally ||
      locallyReadAlertsByIndex.includes(index)
    );
  };

  const userAlerts = useMemo(() => {
    return userAlertPages
      ? userAlertPages.map(response => response.userAlerts).flat()
      : [];
  }, [userAlertPages]);

  return (
    <div className={styles.userAlerts}>
      <Dropdown
        dropdownRender={optionsNode => (
          <UserAlertListDropdown
            hasAlerts={!!userAlerts.length}
            isLoading={isFetchingAlerts}
            onClickMarkUserAlertsRead={onClickMarkUserAlertsRead}
            optionsNode={optionsNode}
          />
        )}
        menu={{
          className: dropdownStyles.userAlertListWrapper,
          items: userAlerts.map((userAlert, index) => ({
            className: styles.userAlertListItemWrapper,
            key: index,
            label: (
              <UserAlertListItem
                index={index}
                isRead={isAlertRead}
                onClick={onClickAlert}
                userAlert={userAlert}
              />
            )
          }))
        }}
        onOpenChange={onToggleDropdown}
        placement="bottomRight"
        trigger={["click"]}
      >
        <div className={styles.bellIconWrapper}>
          <BellIcon className={styles.bellIcon} height="22px" width="22px" />
          {hasUnviewed && !hasOpenedAlertsOnce && (
            <div className={styles.bellIconUnreadSymbol} />
          )}
        </div>
      </Dropdown>
    </div>
  );
}
